import {Template} from "../../../../components/template/template.jsx";
import {Card} from "../../../../components/card/card.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import React, {useEffect, useState} from "react";
import {useGeneralInput, useGeneralInputSpecialCase} from "../../../../hooks/use-general-input.js";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {buildJanitorialFormData, buildJanitorialObj, checkInstalledItems, initialJanitorialForm, statusArr, validateEquipmentsFields} from "../janitorial-utils.js";
import InputDatetime from "../../../../components/input/input-datetime.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import Attach from "../../../../components/attach/attach.jsx";
import RegisteredLocation from "../../../../components/registered-location/registered-location.jsx";
import JanitorialItemList from "./components/janitorial-item-list.jsx";
import {useParams, useSearchParams} from "react-router";
import janitorialService from "../../../../services/web/janitorial-service.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import {FaPlus, FaPrint} from "react-icons/fa";
import {useJanitorialQueries} from "./use-janitorial-queries.js";
import {useEntityMutation} from "../../../../hooks/use-entity-mutation.js";
import EntityActionCard from "../../entity-action-card/entity-action-card.jsx";
import destroyUtils from "../../../../utils/destroy-utils.js";
import MySwal from "../../../../components/swal/my-swal.jsx";
import JanitorialCreateTaskModal from "./components/janitorial-create-task-modal.jsx";
import CloseTaskModal from "../../../../components/modal/close-dispatch-task.jsx";
import {toast} from "react-toastify";
import Alert from "../../../../components/alert/alert.jsx";
import {finishDashboardEdit} from "../../../../utils/dashboard-edit-window.js";

const JanitorialForm = () => {
  const queryClient = useQueryClient();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const redir = searchParams.get("redir");
  const [form, setForm] = useState(initialJanitorialForm);
  const [equipments, setEquipments] = useState([])
  const [file, setFile] = useState(null);
  const navigate = navigateHook();
  const [removedFile, setRemovedFile] = useState(null);
  const handleInput = useGeneralInput(setForm);
  const handleSpecialInput = useGeneralInputSpecialCase(setForm);
  const [showModal, setShowModal] = useState(false);
  const BASENAME = import.meta.env.BASE_URL;
  const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB.replace(/^\//, "");
  const {
    agents,
    reasons,
    corporation,
    janitorialData,
    equipmentData,
    currentUser,
    equipmentsOptions,
    isGlobalLoading,
    activeTask,
    dispatchEnabled,
    getJanitorialTaskById,
    registeredAddress
  } = useJanitorialQueries(id)

  const isDispatched = dispatchEnabled?.inProgress === true;
  const isAssigned = dispatchEnabled?.assigment === true;

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedTaskToClose, setSelectedTaskToClose] = useState(null);

  const handleOpenCloseModal = (task) => {
    setSelectedTaskToClose(task);
    setShowCloseModal(true);
  };

  const onChangeFile = (uploadedFile) => {
    if (uploadedFile) setFile(uploadedFile);
  }

  const removeFile = (fileUploaded) => {
    if (fileUploaded) {
      setRemovedFile(fileUploaded);
      setForm(prev => ({...prev, janitorial_file: null, janitorial_file_attach: null}));
    }
  }

  useEffect(() => {
    const cityRaw = form?.city || "";

    if (!cityRaw) {
      if (form?.district !== "") {
        handleSpecialInput("district", "");
      }
      return;
    }
    const [_, stateName] = cityRaw.split(" - ");
    const currentDistrict = stateName ? stateName.trim() : "";

    if (form?.district !== currentDistrict) {
      handleSpecialInput("district", currentDistrict);
    }
  }, [form?.city]);

  const createTaskMutation = useMutation({
    mutationFn: (payload) => janitorialService.createDispatchTask(payload),
    onSuccess: (data) => {
      toast.success("Salvo com sucesso");
      setShowModal(false);
      queryClient.invalidateQueries({ queryKey: ['janitorial', id] });
      queryClient.invalidateQueries({ queryKey: ['janitorial-task', id] });
      queryClient.invalidateQueries({ queryKey: ['janitorial-dispatch'] });
    },
    onError: (error) => {
      console.error("Erro ao criar tarefa:", error);
      let message = "Erro ao criar tarefa no despacho";
      if (error.response.status !== 500) {
        message = error.response.data.errorMessages ?? error.response.data.message;
      }
      projectUtils.swalErrorMessage("Erro", message, "Ok");
    }
  });

  const hasLinkedTasks = Array.isArray(getJanitorialTaskById) && getJanitorialTaskById.length > 0;

  const handleGenerateTask = async (selectedDate) => {

    if (activeTask) {
      projectUtils.swalErrorMessage(
          "Atenção",
          "Já existe uma tarefa ativa no despacho para esta zeladoria. Cancele-a antes de gerar uma nova.",
          "Ok"
      );
      return;
    }

    const payload = {
      source: "JANITORIAL",
      sourceId: janitorialData?.id,
      reason: form?.reason,
      sourceDescription: janitorialData?.id,
      place: form?.place
          ? (registeredAddress.find(address => String(address.value) === String(form.place))?.label ?? null)
          : null,
      district: form?.district,
      city: form?.city,
      neighborhood: form?.neighborhood,
      street: form?.street,
      number: form?.number,
      latitude: form?.latitude,
      longitude: form?.longitude,
      dtToCreate: selectedDate
          ? new Date(selectedDate).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }).replace(',', '')
          : null,
    };
    createTaskMutation.mutate({userId: currentUser?.id, model : payload});
  };

  const removeInstalledItems = () => {
    const itensQuantity = equipments
      .filter(equipment => equipment.action === "INSTALLATION")
      .reduce((acc, currentValue) => {
        acc[currentValue.item] = (acc[currentValue.item] || 0) + currentValue.quantity;
        return acc;
      }, {});

    const itensRemoved = equipments
      .filter(equipment => equipment.action !== "INSTALLATION")
      .reduce((acc, currentValue) => {
        acc[currentValue.item] = (acc[currentValue.item] || 0) + currentValue.quantity;
        return acc;
      }, {});

    const itensToRemove = Object.keys(itensQuantity).reduce((acc, item) => {
      const installed = itensQuantity[item] || 0;
      const removed = itensRemoved[item] || 0;

      const difference = installed - removed;

      if (difference > 0) {
        acc[item] = difference;
      }

      return acc;
    }, {});

    const addMissingRemovals = () => {
      return Object.entries(itensToRemove).map(
        ([item, quantity]) => ({
          item: Number(item),
          quantity,
          action: "WITHDRAWAL",
          date: new Date(),
          user: form.user_start
        })
      );
    };

    return addMissingRemovals();
  };

  const mutation = useEntityMutation({
    service: janitorialService,
    getInvalidateKeys: (data, variables) => {
      if(!!id && variables.method === "update") {
        return [
          ['janitorial', id],
          ['janitorial-equipments', id],
        ]
      }
      else return null
    },
    navigatePath: "janitorial"
  });

  const save = async (afterSave = null, showSuccessAlert = true) => {
    const itensValidation = validateEquipmentsFields(equipments);
    if(itensValidation) {
      projectUtils.swalErrorMessage("", itensValidation, "Ok");
      return;
    }

    let removedItens = [];

    if(form.district === "" || form.city === ""){
      projectUtils.swalErrorMessage("", "Informe um município com estado, no formato \"CIDADE - ESTADO\"", "Ok");
      return;
    }

    if(form.status === "CLOSED" && checkInstalledItems(equipments)){
      const result = await MySwal.fire({
        icon: "warning",
        title: "Atenção",
        html: "Os equipamentos instalados devem ser removidos ao finalizar esta solicitação? <br/>Para confirmar, digite <b>remover</b> abaixo:",
        input: "text",
        showDenyButton: true,
        confirmButtonText: "Remover",
        denyButtonText: "Manter",
        confirmButtonColor: "#0d3b66",
        denyButtonColor: "#4B5563",
        reverseButtons: true,
        preConfirm: (inputValue) => {
          if (inputValue.trim() !== "remover") {
            MySwal.showValidationMessage("Você precisa digitar 'remover'");
            return false;
          }
          return true;
        }
      });

      if (result.isDismissed) {
        return;
      }

      if (result.isConfirmed) {
        removedItens = removeInstalledItems();
      }
    }

    const formData = buildJanitorialFormData(removedItens, form, equipments, file);

    if(id){
      formData.append("_method", "PUT");
      mutation.mutate({method: "update", payload: formData, id, showSuccessAlert}, {
        onSuccess: () => {
          if (typeof afterSave === "function") {
            afterSave();
            return;
          }

          finishDashboardEdit(redir, navigate, "Zeladoria salva com sucesso.");
        }
      });
    } else {
      mutation.mutate({method: "create", payload: formData, id: null, showSuccessAlert}, {
        onSuccess: () => {
          if (typeof afterSave === "function") afterSave();
        }
      });
    }
  }

  const destroy = async () => {
    if (!id) return;
    const result = await destroyUtils.simpleDestroy("Excluir a solicitação?");
    if (!result.isConfirmed) return;
    mutation.mutate({method: "delete", payload: null, id});
  };

  useEffect(() => {
    if(!corporation && id) return;
    handleSpecialInput("city", corporation?.city ?? "");
  }, [corporation]);

  useEffect(() => {
    if (!id && currentUser?.id) {
      handleSpecialInput("user_start", currentUser.id);
    }
  }, [currentUser?.id, id]);

  useEffect(() => {
    if(!janitorialData) return;
    setForm(buildJanitorialObj(janitorialData));
  }, [janitorialData]);

  useEffect(() => {
    if(!equipmentData) return;
    setEquipments(equipmentData)
  }, [equipmentData]);

    return(
        <Template isLoading={isGlobalLoading|| mutation.isPending}>
            <Template.Header>
                <Template.Title>
                    Solicitação de zeladoria
                </Template.Title>
              {!!id &&
                <div className="flex gap-2">
                  <Button title="Imprimir" onClick={() => {
                    navigate(`/reports/janitorial_print/${id}`, true)
                  }}>
                    <FaPrint/>
                  </Button>
                  {janitorialData?.status === "OPEN" && !dispatchEnabled?.inProgress && !activeTask && (
                      <Button
                          target="primary-outline"
                          onClick={() => {
                            save(() => setShowModal(true), false);
                          }}
                      >
                        <FaPlus/> Agendar tarefa
                      </Button>
                  )}
                  <Button target="primary-outline" onClick={() => {
                    navigate("/janitorial/form")
                    setEquipments([])
                    setForm(initialJanitorialForm)
                  }}>
                    <FaPlus/> Adicionar solicitação
                  </Button>
                </div>
              }
            </Template.Header>
            <Template.Body>
              <Card>
                <Card.Header>
                  <Card.Title>
                    Geral
                  </Card.Title>
                </Card.Header>
                <Card.Body className="space-y-4">
                  <div className="flex gap-4 flex-wrap">
                    <div className="flex-2 min-w-[160px]">
                      <SelectComponent
                        id="user_start"
                        options={agents}
                        label="Agente da abertura *"
                        value={agents.find(agent => agent.value === form.user_start) ?? undefined}
                        onChange={option => handleSpecialInput("user_start", option?.value)}
                      />
                    </div>

                    <div className="flex-2 min-w-[190px]">
                      <InputDatetime
                        id={"date_start"}
                        label={"Data/hora de abertura *"}
                        selected={form.date_start}
                        dateFormat="LL"
                        onChange={(date) => handleSpecialInput("date_start", date)}
                      />
                    </div>

                    <div className="flex-2 min-w-[148px]">
                      <SelectComponent
                        id="status"
                        options={statusArr}
                        label="Status *"
                        value={statusArr.find(option => option.value === form.status) ?? undefined}
                        onChange={option => handleSpecialInput("status", option?.value)}
                      />
                    </div>

                    <div className="flex-6 min-w-[200px]">
                      <InputForm
                        id="requester"
                        label="Solicitante"
                        value={form.requester}
                        onChange={handleInput}
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 flex-wrap mt-2">
                    <div className="flex-4 min-w-[160px]">
                      <SelectComponent
                        id="reason"
                        label="Natureza *"
                        value={reasons.find(reason => reason.value === form.reason)}
                        options={reasons}
                        onChange={option => handleSpecialInput("reason", option?.value)}
                      />
                    </div>

                    <div className="flex-4 min-w-[220px]">
                      <SelectComponent
                        id="user_end"
                        label="Agente de encerramento"
                        options={agents}
                        value={agents.find(agent => agent.value === form.user_end) ?? undefined}
                        onChange={option => handleSpecialInput("user_end", option?.value)}
                      />
                    </div>

                    <div className="flex-2 min-w-[220px]">
                      <InputDatetime
                        id="date_end"
                        label="Data/hora de encerramento"
                        selected={form.date_end}
                        onChange={date => handleSpecialInput("date_end", date)}
                      />
                    </div>

                    <div className="w-[160px]">
                      <Attach onChangeFile={onChangeFile} removeFile={removeFile}
                              itemId={`janitorial-img`} attachId={form.janitorial_file_attach} />
                    </div>
                  </div>

                  <div className="flex mt-2">
                    <div className="flex-1">
                      <InputTextarea
                        id="description"
                        label="Descrição"
                        value={form.description}
                        onChange={handleInput}
                        textAreaClass="h-[150px]"
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>

              <RegisteredLocation
                cardClassName="mt-2"
                data={form}
                setData={setForm}
                corporation={corporation}
                showConfirmLocationAndAddress={false}
              />

              {Array.isArray(getJanitorialTaskById) && getJanitorialTaskById.length > 0 && (
                <Card className="mt-2">
                  <Card.Header>
                    <Card.Title>
                      Tarefas
                    </Card.Title>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="w-full overflow-x-auto">
                      <table className="w-full text-left border-collapse border border-gray-200 table-fixed">
                        <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="p-3 text-[13px] font-bold text-gray-700 border-r border-gray-200 w-[15%]">Data/Hora criação</th>
                            <th className="p-3 text-[13px] font-bold text-gray-700 border-r border-gray-200 w-[15%]">Data/Hora agendamento</th>
                          <th className="p-3 text-[13px] font-bold text-gray-700 border-r border-gray-200 w-[15%]">Status</th>
                          <th className="p-3 text-[13px] font-bold text-gray-700 border-r border-gray-200 w-[15%]">Natureza</th>
                          <th className="p-3 text-[13px] font-bold text-gray-700 border-r border-gray-200 w-[10%]">Despacho</th>
                          <th className="p-3 text-[13px] font-bold text-gray-700 border-r border-gray-200 w-[45%]">Motivo encerramento</th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {Array.isArray(getJanitorialTaskById) && getJanitorialTaskById.map((task, index) => {
                          return (
                              <tr key={task.id || index} className="hover:bg-gray-50 transition-colors">
                                <td className="p-3 text-[13px] text-gray-600 border-r border-gray-200 truncate">
                                  {task.dtCreated}
                                </td>
                                <td className="p-3 text-[13px] text-gray-600 border-r border-gray-200 truncate">
                                  {task.dateToCreate}
                                </td>

                                <td className="p-3 text-[13px] text-gray-600 border-r border-gray-200">
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <span
                                          className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${task.status ? 'bg-[#106b10]' : 'bg-[#d1d1d1]'}`}
                                          aria-hidden="true"
                                      ></span>
                                      <span className="truncate">{task.statusText ?? ""}</span>
                                    </div>
                                    {task.status === true && (
                                        <Button
                                            target="secondary"
                                            disabled={
                                                isDispatched ||
                                                isAssigned
                                            }
                                            className="mx-1 hover:!bg-[#ed5565] !bg-red-700 focus:!ring-red-300 !py-1 !px-2.5 !text-[11px] h-auto flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:!bg-gray-400 disabled:!border-gray-400 disabled:hover:!bg-gray-400"
                                            onClick={() => handleOpenCloseModal(task)}
                                        >
                                          Encerrar
                                        </Button>
                                    )}
                                  </div>
                                </td>

                                <td className="p-3 text-[13px] text-gray-600 border-r border-gray-200 truncate">
                                  {task.reasonText}
                                </td>

                                <td className="p-3 text-[13px] text-gray-600 border-r border-gray-200 truncate">
                                  <a
                                      href={`${BASENAME}${API_DESPACHO_WEB}/dispatch/form/${task.dispatchId}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-600 underline hover:text-blue-800"
                                  >
                                    {task.numberCheckbook}
                                  </a>
                                </td>

                                <td className="p-3 text-[13px] text-gray-600 border-r border-gray-200 break-words whitespace-pre-wrap">
                                  {task.cancelObservation}
                                </td>
                              </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
              )}

              <Card className="mt-2">
                <Card.Header>
                  <Card.Title>
                    Equipamentos
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <div className="grid-cols-12">
                    <h4 className="font-bold text-[14px]">Instalados</h4>
                    <div className="border-b border-b-[#d9dada] mb-3"></div>
                    <JanitorialItemList
                      items={equipments}
                      setItems={setEquipments}
                      agents={agents}
                      equipments={equipmentsOptions}
                    />
                  </div>

                  { equipments.filter(equipment => equipment.action !== "INSTALLATION").length > 0 &&
                    <div className="mt-2 grid-cols-12">
                      <h4 className="font-bold text-[14px]">Removidos</h4>
                      <div className="border-b border-b-[#d9dada] mb-3"></div>
                      <JanitorialItemList
                        operation="DESTROY"
                        items={equipments}
                        setItems={setEquipments}
                        agents={agents}
                        equipments={equipmentsOptions}
                      />
                    </div>
                  }
                </Card.Body>
              </Card>

              <EntityActionCard>
                <div className="w-full flex items-center justify-end gap-4">

                  {isDispatched ? (
                      <Alert className="p-[5px_10px]! text-right" target="warning">
                        Não é possível editar ou excluir, pois existe uma tarefa com despacho em aberto.
                      </Alert>
                  ) : isAssigned ? (
                      <Alert className="p-[5px_10px]! text-right" target="warning">
                        Não é possível editar, pois existe uma tarefa com atribuição em aberto.
                      </Alert>
                  ) : hasLinkedTasks  || janitorialData?.has_attendance? (
                      <Alert className="p-[5px_10px]! text-right" target="warning">
                        Não é possível excluir, pois existem tarefas ou atendimentos associados.
                      </Alert>
                  ) : null}

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!!id && (
                        <EntityActionCard.Button
                            onClick={destroy}
                            disabled={isDispatched || isAssigned || activeTask || hasLinkedTasks || janitorialData?.has_attendance}
                            target="danger"
                            label="Excluir"
                        />
                    )}
                    <EntityActionCard.Button
                        onClick={save}
                        disabled={isDispatched || isAssigned}
                        label="Salvar"
                    />
                  </div>
                </div>
              </EntityActionCard>
            </Template.Body>

          <JanitorialCreateTaskModal
              open={showModal}
              setOpen={setShowModal}
              onConfirm={handleGenerateTask}
          />

          <CloseTaskModal
              open={showCloseModal}
              setOpen={setShowCloseModal}
              task={selectedTaskToClose}
              currentUser={currentUser}
              janitorialId={id}
          />

        </Template>
    )
}

export default JanitorialForm;